import { AfterViewChecked, Component, ElementRef, ViewChild, computed, inject, signal } from '@angular/core';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { GmrDataService } from '../core/gmr-data.service';
import { HashStateService } from '../core/hash-state.service';
import { DownloadService } from '../core/download.service';
import { ThirdPartyIntegrationsService } from '../core/third-party-integrations.service';
import { UiStateService } from '../core/ui-state.service';
import { DecodeEntitiesPipe } from '../shared/pipes/decode-entities.pipe';
import { ParameterFieldComponent } from './parameter-field.component';
import { isParameterMissing } from './parameter-validation';

//
// 1:1 port of the .step2 block (breadcrumb, heading, explanation, the native <form>, parameter
// list, submit button) in menu.html.
//
// Two overlapping legacy click-handling paths existed for the download button in the original:
// AngularJS's own ng-click="downloadProgress($event)" (writes into the *generic* .errorMessage
// selector, which matches BOTH the er500 and progressDownload modals at once) AND a separate
// plain-jQuery handler bound to ".copyClipboard, .requestButton.download" (writes specifically
// into .errorMessage.progressDownload, on its own independent 2.5s/3.5s auto-hide timers). Both
// fired on every click of the real download button, into overlapping DOM, on independent timers.
// That is accumulated redundancy, not a deliberate two-stage UI - consolidated here into one
// onSubmit() that does the union of the OBSERVABLE effects (bookmark hash + clipboard copy
// + the progress message/spinner sequence - see LoadingService.showDownloadProgress).
//
@Component({
  selector: 'app-parameter-form',
  standalone: true,
  imports: [TranslateDirective, TranslatePipe, DecodeEntitiesPipe, ParameterFieldComponent],
  styles: [':host { display: block; }'],
  templateUrl: './parameter-form.component.html',
})
export class ParameterFormComponent implements AfterViewChecked
{
  protected readonly data = inject(GmrDataService);
  protected readonly ui = inject(UiStateService);
  private readonly hashState = inject(HashStateService);
  private readonly translate = inject(TranslateService);
  private readonly thirdParty = inject(ThirdPartyIntegrationsService);
  private readonly downloads = inject(DownloadService);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  protected readonly myFunction = computed(() => this.data.selectedFunction());
  protected readonly myFunctionParameters = computed(() => this.data.selectedFunctionParameters());
  protected readonly myLovPresets = computed(() => this.data.myLovPresets());

  //
  // Safety net, not the primary mechanism. Validation itself is the browser's: plain inputs carry
  // [required] and the LOV selects get a setCustomValidity message (LovSelectComponent.updateValidity),
  // so an incomplete form never reaches this handler - the browser refuses the submit and shows its own
  // bubble at the offending field. This guard exists because that reporting hangs off one element inside
  // a third-party widget: should ng-select ever stop rendering it, the download must still be refused
  // rather than silently run with no company selected. It is also why the check is duplicated in
  // parameter-validation.ts rather than inferred from form.checkValidity().
  //
  // The original had `ng-click='presets.$valid'` on the download button - a no-op expression evaluation,
  // not a guard - so an empty required field never blocked a download there at all.
  //
  // A plain getter, not computed(): ng-select/ngModel mutate cfr_default_value(_arr) in place on the
  // existing parameter objects rather than replacing the selectedFunctionParameters signal's array,
  // exactly like ParameterFieldComponent's own isChecked/isRequired getters - a computed() would only
  // re-run when that signal is actually reassigned, which cascadeDependents does for a DEPENDENT
  // parameter but never for the field the user just changed themselves.
  //
  protected get canDownload(): boolean
  {
    return this.myFunctionParameters().every(param => !isParameterMissing(param));
  }

  @ViewChild('form') private formRef?: ElementRef<HTMLFormElement>;
  @ViewChild('explanationContent') private explanationRef?: ElementRef<HTMLElement>;

  //
  // 1:1 port of the exHeight > 54 check that adds the maxHeight/truncation class, plus the
  // .explanationContent.maxHeight click-to-expand handler.
  //
  protected readonly explanationTruncatable = signal(false);
  protected readonly explanationExpanded = signal(false);
  private measuredExplanationFor: unknown = undefined;
  private measuredHintOverflowFor: unknown = undefined;
  private scannedFormsFor: unknown = undefined;

  ngAfterViewChecked(): void
  {
    const fn = this.myFunction();
    if (fn && this.explanationRef && this.measuredExplanationFor !== fn)
    {
      this.measuredExplanationFor = fn;
      this.explanationTruncatable.set(this.explanationRef.nativeElement.scrollHeight > 54);
    }
    if (fn && this.measuredHintOverflowFor !== fn)
    {
      this.measuredHintOverflowFor = fn;
      this.markOverflowingUserHints();
    }

    //
    // Matomo's Form Analytics is enabled in the tag-manager container, but it scans for forms once,
    // at the container's own page view - at which point this form does not exist, because it is only
    // rendered when a report is opened. Without a rescan the Forms report stays empty for the entire
    // catalogue, which is exactly what it did.
    //
    // Here rather than in a signal effect because the scan reads the DOM: the form and every one of
    // its fields have to be in place, and this hook is the point at which the framework guarantees
    // they are. Keyed on the function, following the two measurements above - switching reports
    // replaces the fields, so the new form has to be scanned, and nothing else may trigger a scan.
    //
    if (fn && this.scannedFormsFor !== fn)
    {
      this.scannedFormsFor = fn;
      this.thirdParty.scanForms();
    }
  }

  //
  // 1:1 port of $scope.addUserHintClass(): each parameter's hint tooltip (.userhintcontent) is
  // momentarily forced visible off-screen (visibility:hidden, so nothing flashes) at its natural
  // 200px width to measure whether it would overflow past the parameter form's right edge. If so,
  // .overflowing is added, which menu.css flips to `right: 36px` instead of the default
  // flows-rightward-from-the-? position - showing the tooltip inward instead of past the edge.
  //
  private markOverflowingUserHints(): void
  {
    const formEl = this.elementRef.nativeElement.querySelector('#chooseFilter .form.presets');
    if (!formEl) return;
    const parentRect = formEl.getBoundingClientRect();

    this.elementRef.nativeElement.querySelectorAll<HTMLElement>('.userhintcontent').forEach(content =>
    {
      content.classList.remove('overflowing');
      content.style.visibility = 'hidden';
      content.style.display = 'block';
      content.style.width = '200px';

      if (content.getBoundingClientRect().right >= parentRect.right)
      {
        content.classList.add('overflowing');
      }

      content.style.visibility = '';
      content.style.display = '';
      content.style.width = '';
    });
  }

  toggleExplanation(): void
  {
    if (this.explanationTruncatable())
    {
      this.explanationExpanded.update(v => !v);
    }
  }

  formMethod(): string
  {
    return this.myFunction()?.cfn_form_method ?? 'GET';
  }

  actionButtonLabel(): string
  {
    const fn = this.myFunction();
    return (fn?.cfn_action_button_label_i18n as string | undefined) ?? 'Download';
  }

  protected readonly showCopiedToast = signal(false);

  onCopyBookmarkClick(): void
  {
    if (!this.formRef) return;
    this.hashState.writeBookmark(this.formRef.nativeElement);
    this.copyCurrentUrlToClipboard();

    //
    // Replaces a vendor buffer flush that had nothing to flush. Copying a bookmark is a real
    // user action and a strong signal - somebody who bookmarks a parameterised report intends to
    // run it again - so it is now reported rather than merely no longer broken.
    //
    this.thirdParty.trackEvent('Report', 'Bookmark copied', this.myFunction()?.cfn_preset_name);

    this.showCopiedToast.set(true);
    setTimeout(() => this.showCopiedToast.set(false), 1500);
  }

  //
  // Bound to the form's own submit event rather than to the buttons' click, which matters twice over: it
  // catches an implicit submit (Enter in a text field) as well as both download buttons, and it runs
  // after native validation, so the progress modal below cannot appear for a submit the browser refused.
  //
  // A missing required value used to be handled by [disabled] on both buttons. That enforced the rule
  // but told the user nothing: the stylesheet has no :disabled styling for .requestButton, so the button
  // looked perfectly normal and clicking it simply did nothing at all.
  //
  onSubmit(event: Event): void
  {
    if (!this.canDownload)
    {
      event.preventDefault();
      return;
    }

    const fn = this.myFunction();
    if (!this.formRef || !fn) return;

    //
    // writeBookmark() already serialises the whole form - that is what it puts in the bookmark hash -
    // so the request summary the download entry carries is its return value rather than a second,
    // separately-maintained serialisation that could disagree with the bookmark.
    //
    const requestSummary = this.hashState.writeBookmark(this.formRef.nativeElement);

    this.copyCurrentUrlToClipboard();

    //
    // No tracking call here: the vendor flush this replaces reported nothing, and the download
    // itself is reported by DownloadService - which is also the only place that knows whether the
    // submit was accepted at all, since an identical request already running is refused.
    //
    const accepted = this.downloads.start(fn, this.formRef.nativeElement, requestSummary);

    //
    // Which of the two routes runs is decided by cfn_form_method and nothing else, inside
    // DownloadService (design D-5):
    //
    // - a GET report is fetched, so this submit must NOT proceed - the request is issued from
    //   JavaScript, which is what makes a real start, a real finish and a real failure observable;
    // - a POST report keeps the native submit, which is a navigation, so the browser reports the
    //   upload itself with a percentage this code cannot obtain. Then the submit proceeds, into a
    //   per-download hidden iframe the service has just pointed the form's target at - one iframe
    //   each, because sharing one meant a second submit cancelled a first request that was still
    //   generating.
    //
    // A refused submit (an identical download already running) is preventDefault()ed as well, or the
    // native route would run the report a second time behind the refusal.
    //
    if (!accepted || fn.cfn_form_method?.toUpperCase() !== 'POST')
    {
      event.preventDefault();
    }
  }

  private copyCurrentUrlToClipboard(): void
  {
    const input = document.createElement('input');
    document.body.appendChild(input);
    input.value = window.location.href;
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }
}
